/**
 * Timeline editing operations on clips.
 *
 * Every operation mutates a `Project` in place and is meant to be run inside an immer recipe
 * (see `EditorDocument.apply`). Operations validate their input and throw `EditError` for
 * illegal edits; the surrounding transaction is then discarded so the document stays consistent.
 */
import { shiftAnimatable, splitAnimatable } from '../animation/keyframes.ts';
import { cloneData } from '../model/clone.ts';
import { cloneClip } from '../model/factory.ts';
import { newId } from '../model/ids.ts';
import { framesToSeconds, secondsToFrame } from '../model/time.ts';
import type {
  BlendMode,
  Clip,
  FitMode,
  Id,
  Params,
  Project,
  TextStyle,
  Track,
  TrackKind,
} from '../model/schema.ts';
import {
  clipEnd,
  clipIndexAt,
  getClip,
  getTrack,
  headroom,
  maxClipDuration,
  wouldCreateCycle,
} from '../timeline/queries.ts';
import { EditError, invariant } from './errors.ts';
import { normalizeTrack } from './normalize.ts';

export type InsertMode = 'overwrite' | 'insert';

export function trackAccepts(kind: TrackKind, clip: Clip): boolean {
  return kind === 'audio' ? clip.type === 'audio' : clip.type !== 'audio';
}

export function assertEditable(track: Track): void {
  if (track.locked) throw new EditError(`Track "${track.name}" is locked`);
}

/** Moves the clip's in-point to `newStart` while keeping its out-point (adjusts source offset). */
export function setClipHead(clip: Clip, newStart: number, project: Project): void {
  const delta = newStart - clip.start;
  invariant(delta < clip.duration, 'Clip would become empty');
  const fps = project.settings.fps;
  if (clip.type === 'video' || clip.type === 'audio') {
    clip.sourceIn = Math.max(0, clip.sourceIn + framesToSeconds(delta, fps) * clip.speed);
  } else if (clip.type === 'sequence') {
    clip.sourceIn = Math.max(0, clip.sourceIn + delta);
  }
  for (const anim of Object.values(clip.params)) if (anim) shiftAnimatable(anim, -delta);
  clip.start = newStart;
  clip.duration -= delta;
}

/** Splits `clip` at absolute `frame`, returning the new right-hand clip (already inserted). */
function splitInTrack(project: Project, track: Track, clip: Clip, frame: number): Clip {
  const rel = frame - clip.start;
  invariant(rel > 0 && rel < clip.duration, 'Split point must be inside the clip');
  const right = cloneData(clip) as Clip;
  right.id = newId('clp');
  const leftParams: Params = {};
  const rightParams: Params = {};
  for (const [key, anim] of Object.entries(clip.params) as Array<[keyof Params, Params[keyof Params]]>) {
    if (!anim) continue;
    const [l, r] = splitAnimatable(anim, rel);
    leftParams[key] = l;
    rightParams[key] = r;
  }
  clip.params = leftParams;
  right.params = rightParams;
  right.start = frame;
  right.duration = clip.duration - rel;
  if (right.type === 'video' || right.type === 'audio') {
    right.sourceIn += framesToSeconds(rel, project.settings.fps) * right.speed;
  } else if (right.type === 'sequence') {
    right.sourceIn += rel;
  }
  clip.duration = rel;
  const idx = track.clips.indexOf(clip);
  track.clips.splice(idx + 1, 0, right);
  // The outgoing transition now belongs to the right-hand piece.
  for (const tr of track.transitions) if (tr.fromClipId === clip.id) tr.fromClipId = right.id;
  return right;
}

/** Removes everything in `[start, end)` from the track, trimming/splitting clips at the boundaries. */
export function clearRange(project: Project, track: Track, start: number, end: number): void {
  if (end <= start) return;
  for (const clip of [...track.clips]) {
    const cEnd = clipEnd(clip);
    if (cEnd <= start || clip.start >= end) continue;
    if (clip.start >= start && cEnd <= end) {
      track.clips.splice(track.clips.indexOf(clip), 1);
    } else if (clip.start < start && cEnd > end) {
      splitInTrack(project, track, clip, end);
      clip.duration = start - clip.start;
    } else if (clip.start < start) {
      clip.duration = start - clip.start;
    } else {
      setClipHead(clip, end, project);
    }
  }
}

/** Shifts every clip starting at or after `from` by `delta` frames. */
function shiftClipsFrom(track: Track, from: number, delta: number): void {
  for (const c of track.clips) if (c.start >= from) c.start += delta;
}

function clampDuration(project: Project, clip: Clip): void {
  const max = maxClipDuration(project, clip);
  if (clip.duration > max) clip.duration = max;
}

export function insertClip(project: Project, trackId: Id, clip: Clip, mode: InsertMode = 'overwrite'): Clip {
  const { sequence, track } = getTrack(project, trackId);
  assertEditable(track);
  invariant(trackAccepts(track.kind, clip), `A ${clip.type} clip cannot be placed on a ${track.kind} track`);
  if (clip.type === 'sequence') {
    invariant(!wouldCreateCycle(project, sequence.id, clip.sequenceId), 'A sequence cannot contain itself');
  }
  if ('mediaId' in clip) invariant(project.media[clip.mediaId], 'Media is not in the project library');
  clip.start = Math.max(0, Math.round(clip.start));
  clampDuration(project, clip);
  if (mode === 'insert') {
    const spanning = track.clips[clipIndexAt(track, clip.start)];
    if (spanning && spanning.start < clip.start) splitInTrack(project, track, spanning, clip.start);
    shiftClipsFrom(track, clip.start, clip.duration);
  } else {
    clearRange(project, track, clip.start, clipEnd(clip));
  }
  track.clips.push(clip);
  normalizeTrack(track);
  return clip;
}

export interface ClipMove {
  clipId: Id;
  trackId: Id;
  start: number;
}

/**
 * Moves one or more clips (possibly across tracks of the same sequence) using overwrite semantics.
 * Transitions between clips that move together onto the same track are preserved.
 */
export function moveClips(project: Project, moves: ClipMove[]): void {
  if (moves.length === 0) return;
  const movingIds = new Set(moves.map((m) => m.clipId));
  const plans = moves.map((m) => {
    const loc = getClip(project, m.clipId);
    const target = getTrack(project, m.trackId);
    invariant(loc.sequence.id === target.sequence.id, 'Clips can only be moved within the same sequence');
    assertEditable(loc.track);
    assertEditable(target.track);
    invariant(
      trackAccepts(target.track.kind, loc.clip),
      `A ${loc.clip.type} clip cannot be placed on a ${target.track.kind} track`,
    );
    return { move: m, loc, target: target.track };
  });
  // Transitions fully inside the moving selection travel with it.
  const carried = plans.flatMap(({ loc }) =>
    loc.track.transitions
      .filter((tr) => tr.fromClipId === loc.clip.id && movingIds.has(tr.toClipId))
      .map((tr) => ({ tr: { ...tr }, sourceTrack: loc.track })),
  );
  for (const { loc } of plans) {
    loc.track.clips.splice(loc.track.clips.indexOf(loc.clip), 1);
  }
  for (const { move, loc, target } of plans) {
    const clip = loc.clip;
    clip.start = Math.max(0, Math.round(move.start));
    clearRange(project, target, clip.start, clipEnd(clip));
    target.clips.push(clip);
    normalizeTrack(target);
  }
  for (const { tr, sourceTrack } of carried) {
    const fromTrack = plans.find((p) => p.loc.clip.id === tr.fromClipId)!.target;
    const toTrack = plans.find((p) => p.loc.clip.id === tr.toClipId)!.target;
    sourceTrack.transitions = sourceTrack.transitions.filter((t) => t.id !== tr.id);
    if (fromTrack === toTrack) fromTrack.transitions.push(tr);
  }
  for (const { loc, target } of plans) {
    normalizeTrack(loc.track);
    normalizeTrack(target);
  }
}

export interface TrimOptions {
  /** Shift following clips to close/open the gap (ripple edit). */
  ripple?: boolean;
}

/** Trims a clip edge to an absolute timeline frame, clamped to media, neighbours and frame 0. */
export function trimClip(
  project: Project,
  clipId: Id,
  edge: 'start' | 'end',
  frame: number,
  options: TrimOptions = {},
): void {
  const { track, clip, index } = getClip(project, clipId);
  assertEditable(track);
  const target = Math.round(frame);
  if (edge === 'end') {
    const oldEnd = clipEnd(clip);
    let newEnd = Math.max(clip.start + 1, Math.min(target, clip.start + maxClipDuration(project, clip)));
    const next = track.clips[index + 1];
    if (!options.ripple && next) newEnd = Math.min(newEnd, next.start);
    clip.duration = newEnd - clip.start;
    if (options.ripple) shiftClipsFrom(track, oldEnd, newEnd - oldEnd);
  } else {
    const oldStart = clip.start;
    let newStart = Math.min(clipEnd(clip) - 1, Math.max(target, clip.start - headroom(project, clip)));
    newStart = Math.max(0, newStart);
    const prev = track.clips[index - 1];
    if (!options.ripple && prev) newStart = Math.max(newStart, clipEnd(prev));
    if (newStart === oldStart) return;
    const after = clipEnd(clip);
    setClipHead(clip, newStart, project);
    if (options.ripple) {
      const delta = newStart - oldStart;
      clip.start = oldStart;
      shiftClipsFrom(track, after, -delta);
    }
  }
  normalizeTrack(track);
}

/** Splits a clip at an absolute timeline frame. Returns the id of the new right-hand clip. */
export function splitClip(project: Project, clipId: Id, frame: number): Id {
  const { track, clip } = getClip(project, clipId);
  assertEditable(track);
  const right = splitInTrack(project, track, clip, Math.round(frame));
  normalizeTrack(track);
  return right.id;
}

/**
 * Razor across a sequence: splits every clip crossing `frame` on the given (or all unlocked) tracks.
 * Returns the ids of the newly created right-hand clips.
 */
export function razor(project: Project, sequenceId: Id, frame: number, trackIds?: Id[]): Id[] {
  const seq = project.sequences[sequenceId];
  invariant(seq, 'Unknown sequence');
  const created: Id[] = [];
  for (const track of [...seq.videoTracks, ...seq.audioTracks]) {
    if (track.locked || (trackIds && !trackIds.includes(track.id))) continue;
    const idx = clipIndexAt(track, frame);
    const clip = track.clips[idx];
    if (clip && clip.start < frame) {
      created.push(splitInTrack(project, track, clip, frame).id);
      normalizeTrack(track);
    }
  }
  return created;
}

/** Deletes clips. With `ripple`, following clips on the same track move left to close the gaps. */
export function deleteClips(project: Project, clipIds: Id[], options: { ripple?: boolean } = {}): void {
  const locs = clipIds.map((id) => getClip(project, id));
  for (const { track } of locs) assertEditable(track);
  // Process right-to-left so ripple shifts don't affect pending removals.
  locs.sort((a, b) => b.clip.start - a.clip.start);
  for (const { track, clip } of locs) {
    const i = track.clips.indexOf(clip);
    if (i === -1) continue;
    track.clips.splice(i, 1);
    if (options.ripple) shiftClipsFrom(track, clipEnd(clip), -clip.duration);
  }
  for (const { track } of locs) normalizeTrack(track);
}

/** Removes the empty gap on a track that contains `frame`, pulling later clips left. */
export function closeGap(project: Project, trackId: Id, frame: number): void {
  const { track } = getTrack(project, trackId);
  assertEditable(track);
  invariant(clipIndexAt(track, frame) === -1, 'There is no gap at this position');
  const prevEnd = track.clips.filter((c) => clipEnd(c) <= frame).reduce((m, c) => Math.max(m, clipEnd(c)), 0);
  const next = track.clips.find((c) => c.start > frame);
  invariant(next, 'There is no gap at this position');
  shiftClipsFrom(track, next.start, prevEnd - next.start);
  normalizeTrack(track);
}

/** Slip: changes which part of the source is shown without moving the clip on the timeline. */
export function slipClip(project: Project, clipId: Id, deltaFrames: number): void {
  const { track, clip } = getClip(project, clipId);
  assertEditable(track);
  const fps = project.settings.fps;
  if (clip.type === 'video' || clip.type === 'audio') {
    const media = project.media[clip.mediaId];
    const maxIn = media?.duration
      ? media.duration - framesToSeconds(clip.duration, fps) * clip.speed
      : Infinity;
    const next = clip.sourceIn + framesToSeconds(deltaFrames, fps) * clip.speed;
    clip.sourceIn = Math.max(0, Math.min(maxIn, next));
  } else if (clip.type === 'sequence') {
    const total = clip.sourceIn + maxClipDuration(project, clip);
    clip.sourceIn = Math.max(0, Math.min(total - clip.duration, clip.sourceIn + deltaFrames));
  } else {
    throw new EditError('Only media and sequence clips can be slipped');
  }
}

/** Roll edit: moves the cut between two adjacent clips, keeping the total length unchanged. */
export function rollEdit(project: Project, leftClipId: Id, frame: number): void {
  const { track, clip: left, index } = getClip(project, leftClipId);
  assertEditable(track);
  const right = track.clips[index + 1];
  invariant(right && right.start === clipEnd(left), 'Roll requires two adjacent clips');
  const minCut = Math.max(left.start + 1, right.start - headroom(project, right));
  const maxCut = Math.min(clipEnd(right) - 1, left.start + maxClipDuration(project, left));
  const cut = Math.max(minCut, Math.min(maxCut, Math.round(frame)));
  if (cut === right.start) return;
  left.duration = cut - left.start;
  setClipHead(right, cut, project);
  normalizeTrack(track);
}

export type ClipPatch = Partial<{
  name: string;
  enabled: boolean;
  muted: boolean;
  fit: FitMode;
  blendMode: BlendMode;
  text: string;
  style: Partial<TextStyle>;
  color: string;
  speed: number;
}>;

/** Updates non-timing clip properties. Changing `speed` rescales the clip duration. */
export function updateClip(project: Project, clipId: Id, patch: ClipPatch): void {
  const { track, clip } = getClip(project, clipId);
  assertEditable(track);
  if (patch.name !== undefined) clip.name = patch.name;
  if (patch.enabled !== undefined) clip.enabled = patch.enabled;
  if (patch.muted !== undefined && 'muted' in clip) clip.muted = patch.muted;
  if (patch.fit !== undefined && 'fit' in clip) clip.fit = patch.fit;
  if (patch.blendMode !== undefined && 'blendMode' in clip) clip.blendMode = patch.blendMode;
  if (clip.type === 'text') {
    if (patch.text !== undefined) clip.text = patch.text;
    if (patch.style) Object.assign(clip.style, patch.style);
  }
  if (clip.type === 'solid' && patch.color !== undefined) clip.color = patch.color;
  if (patch.speed !== undefined && (clip.type === 'video' || clip.type === 'audio')) {
    invariant(patch.speed > 0 && patch.speed <= 16, 'Speed must be between 0 and 16×');
    const sourceSeconds = framesToSeconds(clip.duration, project.settings.fps) * clip.speed;
    clip.speed = patch.speed;
    clip.duration = Math.max(1, secondsToFrame(sourceSeconds / patch.speed, project.settings.fps));
    clampDuration(project, clip);
    const next = track.clips[track.clips.indexOf(clip) + 1];
    if (next && clipEnd(clip) > next.start) clip.duration = next.start - clip.start;
  }
  normalizeTrack(track);
}

/** Duplicates clips right after the end of the selection on their own tracks. Returns new ids. */
export function duplicateClips(project: Project, clipIds: Id[]): Id[] {
  const locs = clipIds.map((id) => getClip(project, id));
  if (locs.length === 0) return [];
  const minStart = Math.min(...locs.map((l) => l.clip.start));
  const maxEnd = Math.max(...locs.map((l) => clipEnd(l.clip)));
  return locs.map(({ track, clip }) => {
    const copy = cloneClip(clip);
    copy.start = maxEnd + (clip.start - minStart);
    return insertClip(project, track.id, copy, 'overwrite').id;
  });
}
